import PropTypes from "prop-types";
import { Link, useNavigate } from "react-router-dom";
import { useRef } from "react";
import SimpleIconBox from "../../image-boxes/simple-icon-box/simple-icon-box.component";
import { ReactComponent as ChatSvg } from "../../../assets/vectors/message-text.svg";
import { nairaFormart } from "../../../utils/functions";

function extractContent(s) {
  var span = document.createElement("span");
  span.innerHTML = s;
  return span.textContent || span.innerText;
}

const PropertyCardItem = ({ data, isAdminView = false }) => {
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);
  
  const video_url = data?.acf?.property_video_url || null;

  let rawExcerpt = extractContent(data?.excerpt?.rendered || "");
  let excerpt = rawExcerpt.length > 140 ? rawExcerpt.substring(0, 140) + "..." : rawExcerpt;

  const addressParts = data?.acf?.address
    ?.split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  const locale_name = data?.acf?.short_locale_name
    ? data?.acf.short_locale_name.split(" ").slice(0, 3).join(" ")
    : addressParts?.length >= 2
    ? addressParts.slice(-2).join(", ")
    : data?.acf?.address || "";

  const gallery = data?.acf?.gallery ? data?.acf.gallery : [];
  
  const hasMedia = gallery.length > 0 || video_url;
  const totalMedia = gallery.length + (video_url ? 1 : 0);

  const scrollNext = (e) => {
    e.preventDefault();
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: scrollContainerRef.current.clientWidth, behavior: "smooth" });
    }
  };

  const scrollPrev = (e) => {
    e.preventDefault();
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -scrollContainerRef.current.clientWidth, behavior: "smooth" });
    }
  };

  const handleMediaClick = () => {
    navigate(isAdminView ? "/dashboard/admin/approvals/" + data?.id : "../properties/" + data?.slug);
  };

  return (
    <div className={`property-card-item flex flex-col shadow-lg rounded-[4px] px-4 py-4`}>
      
      {hasMedia && (
        <div className="w-full h-48 mb-2 rounded-md overflow-hidden relative group">
          <div 
            ref={scrollContainerRef}
            className="flex w-full h-full overflow-x-auto snap-x snap-mandatory [&::-webkit-scrollbar]:hidden"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {gallery.map((img, idx) => {
              const imgSrc = typeof img === "string" ? img : img?.full_image_url || img?.url;
              return (
                <div key={idx} className="min-w-full h-full flex-shrink-0 snap-center relative bg-gray-100">
                  <button
                    type="button"
                    onClick={handleMediaClick}
                    className="w-full h-full block p-0 border-0 bg-transparent cursor-pointer outline-none focus:ring-2 focus:ring-primary"
                    aria-label={`View Property details`}
                  >
                    <img 
                      src={imgSrc} 
                      className="w-full h-full object-cover" 
                      alt={`Property Slide ${idx + 1}`} 
                    />
                  </button>
                </div>
              );
            })}
            
            {video_url && (
              <div className="min-w-full h-full flex-shrink-0 snap-center relative bg-black">
                <span className="absolute top-2 left-2 bg-primary text-white text-[10px] font-bold px-2 py-1 rounded z-10 shadow-md pointer-events-none">
                  Video Tour
                </span>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video
                  src={video_url}
                  className="w-full h-full object-cover"
                  controls
                  playsInline
                  preload="metadata"
                />
              </div>
            )}
          </div>
          
          {totalMedia > 1 && (
            <>
              <button
                type="button"
                onClick={scrollPrev}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 items-center justify-center bg-black/50 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity md:flex hidden outline-none hover:bg-black/80"
              >
                &#10094;
              </button>
              <button
                type="button"
                onClick={scrollNext}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 items-center justify-center bg-black/50 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity md:flex hidden outline-none hover:bg-black/80"
              >
                &#10095;
              </button>
              <div className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] font-bold px-3 py-1 rounded-full md:hidden pointer-events-none shadow-sm tracking-wide">
                Swipe &rarr;
              </div>
            </>
          )}
        </div>
      )}

      <Link to={isAdminView ? "/dashboard/admin/approvals/" + data?.id : "../properties/" + data?.slug}>
        <div className="flex justify-between mt-2 header-font">
          <h4
            className="text-[14px] w-[60%]"
            dangerouslySetInnerHTML={{ __html: data?.title }}
          ></h4>
          <SimpleIconBox text={locale_name} />
        </div>
       <p className="text-[12px] mt-3 font-light mr-8 mb-3 line-clamp-2 break-words">
          {excerpt}
        </p>
      </Link>
      
      {isAdminView && data?.author && (
        <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-600 border border-gray-200">
           <p><span className="font-semibold">Uploader:</span> {data.author.full_name}</p>
        </div>
      )}

      <div className="mt-3 flex justify-between items-center">
        <span className="text-[12px] font-semibold text-secondary">
          {nairaFormart(data?.acf?.price)}{" "}
          <span className="ml-1 font-light word">
            / {data?.acf?.per_duration || "year"}
          </span>
        </span>
        <div className="flex flex-row space-x-3">
          {isAdminView ? (
            <button
              className="rounded-md md:rounded-lg text-center text-white text-sm md:text-[12px] py-2 px-8 md:py-[6px] bg-primary hover:bg-blue-900"
             onClick={() => navigate("/dashboard/admin/approvals/" + data?.id)}
            >
              Review
            </button>
          ) : (
            <>
              <button
                className="rounded-md md:rounded-lg text-center text-white text-sm md:text-[12px] py-2 px-8 md:py-[6px] bg-primary hover:bg-blue-900"
                onClick={() => navigate("../properties/" + data?.slug)}
              >
                View
              </button>
              <button
                className="rounded-md md:rounded-lg text-center text-white text-sm md:text-[12px] py-2 px-4 md:py-[6px] bg-primary hover:bg-blue-900 svg-icon-white"
                onClick={() =>
                  navigate("/dashboard/messages", {
                    state: { hash: data?.author?.hash },
                  })
                }
              >
                <ChatSvg className="text-white" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

PropertyCardItem.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.number,
    content: PropTypes.shape({
      rendered: PropTypes.string,
    }),
    excerpt: PropTypes.shape({
      rendered: PropTypes.string.isRequired,
    }).isRequired,
    acf: PropTypes.shape({
      short_locale_name: PropTypes.string,
      gallery: PropTypes.array,
      price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      per_duration: PropTypes.string,
      property_video_url: PropTypes.string,
    }).isRequired,
    slug: PropTypes.string.isRequired,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    author: PropTypes.shape({
      hash: PropTypes.string,
      full_name: PropTypes.string,
    }),
  }).isRequired,
  isAdminView: PropTypes.bool,
};

export default PropertyCardItem;